import { useEffect, useState } from "react";
import SiteVisitManagement from "./components/SiteVisitManagement";
import { AssignmentData, Venue } from "./types/site-visit";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import apiService from "../../service/Api";
import { logout } from "../../store/slice/AuthSlice";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/ShowToast";
import formatDate from "../../utils/FormatDate";

export interface ApiResponse<T = any> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function SiteVisitList() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");

  const [currentStatus, setCurrentStatus] = useState("pending");

  const fetchVenues = async (status: string) => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage + 1,
        page_size: pageSize,
        status,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (category !== "all") {
        params.venue_type = category;
      }

      const response = await apiService.get<ApiResponse<any>>(
        "/api/venues/site-visit/",
        params
      );

      const results = response.data.results;
      if (!Array.isArray(results)) throw new Error("Invalid API response");

      const mappedVenues: Venue[] = results.map((item: any) => ({
        id: item.id.toString(),
        venueId: item.venue?.id?.toString() ?? item.id.toString(),
        name: item.venue?.name ?? "Unknown Venue",
        type: item.venue?.venue_type ?? "",
        address: [
          item.venue?.address_one,
          item.venue?.address_two,
          item.venue?.city,
          item.venue?.suburb,
          item.venue?.country,
        ]
          .filter(Boolean)
          .join(", "),
        status: item.status,
        assignee: item.assignees?.[0]
          ? `${item.assignees[0].first_name} ${item.assignees[0].last_name}`
          : "",
        dueDate: formatDate(item.scheduled_date_time),
        notes: item.notes ?? "",
      }));

      setVenues(mappedVenues);
      setTotalItems(response.data.count);
    } catch (err: any) {
      // handle errors...
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues(currentStatus);
  }, [currentStatus, searchTerm, category, currentPage, pageSize]);

  const handleAssignVenue = async (
    siteVisitId: number, // use number type
    data: AssignmentData
  ) => {
    setLoading(true);
    setError(null);

    try {
      const assigneeId = parseInt(data.assigneeId, 10);
      if (isNaN(assigneeId)) throw new Error("Invalid assignee ID.");

      const dateObj = new Date(data.dueDate);
      if (isNaN(dateObj.getTime())) throw new Error("Invalid due date.");

      const payload = {
        scheduled_date_time: dateObj.toISOString(),
        assignee_ids: [assigneeId],
        notes: data.notes?.trim() || null,
        status: "assigned",
      };

      // PATCH to specific site visit using its ID
      const response = await apiService.patch(
        `/api/venues/site-visit/${siteVisitId}/`,
        payload
      );

      showSuccessToast("Site visit updated successfully");
      fetchVenues(currentStatus);
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err.message ||
        "Failed to update site visit.";
      console.error("Assignment error:", message);
      showErrorToast(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (
    userId: string,
    venueId: string
  ): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  return (
    <SiteVisitManagement
      venues={venues}
      onAssignVenue={handleAssignVenue}
      onSendEmail={handleSendEmail}
      isLoading={isLoading}
      page={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      currentStatus={currentStatus}
      onStatusChange={setCurrentStatus}
      searchTerm={searchTerm}
      onSearchTermChange={setSearchTerm}
      category={category}
      onCategoryChange={setCategory}
    />
  );
}

export default SiteVisitList;
